import { useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

import markerIconUrl from 'leaflet/dist/images/marker-icon.png';
import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2xUrl,
    iconUrl: markerIconUrl,
    shadowUrl: markerShadowUrl,
});

const DEFAULT_CENTER = [-34.6037, -58.3816];
const DEFAULT_ZOOM = 13;

function MapClickHandler({ onClick }) {
    useMapEvents({
        click(e) {
            onClick([e.latlng.lat, e.latlng.lng]);
        },
    });
    return null;
}

function MapFlyTo({ center }) {
    const map = useMap();
    if (center) {
        map.flyTo(center, map.getZoom() >= 15 ? map.getZoom() : 15);
    }
    return null;
}

export default function MapPicker({ defaultPosition, onChange, className = '' }) {
    const hasDefault = defaultPosition?.lat != null && defaultPosition?.lng != null;
    const [position, setPosition] = useState(
        hasDefault ? [defaultPosition.lat, defaultPosition.lng] : null
    );
    const [searchText, setSearchText] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');

    const handlePositionChange = useCallback((latlng) => {
        setPosition(latlng);
        onChange?.({ lat: latlng[0], lng: latlng[1] });
    }, [onChange]);

    const handleSearch = useCallback(async () => {
        if (!searchText.trim()) return;
        setSearching(true);
        setSearchError('');
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&limit=5`,
                { headers: { 'Accept-Language': 'es' } }
            );
            const data = await res.json();
            if (data.length > 0) {
                const { lat, lon } = data[0];
                handlePositionChange([parseFloat(lat), parseFloat(lon)]);
            } else {
                setSearchError('No se encontraron resultados.');
            }
        } catch {
            setSearchError('Error al buscar.');
        } finally {
            setSearching(false);
        }
    }, [searchText, handlePositionChange]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    }, [handleSearch]);

    return (
        <div className={`space-y-3 ${className}`}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Buscar dirección..."
                    className="pl-9 bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/30"
                />
            </div>
            {searchError && <p className="text-xs text-red-400">{searchError}</p>}
            <div className="h-64 rounded-lg overflow-hidden border border-[#3a3a3f]">
                <MapContainer
                    center={position || DEFAULT_CENTER}
                    zoom={DEFAULT_ZOOM}
                    className="h-full w-full"
                    zoomControl={true}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapClickHandler onClick={handlePositionChange} />
                    {position && <MapFlyTo center={position} />}
                    {position && (
                        <Marker
                            position={position}
                            draggable={true}
                            eventHandlers={{
                                dragend: (e) => {
                                    const { lat, lng } = e.target.getLatLng();
                                    handlePositionChange([lat, lng]);
                                },
                            }}
                        />
                    )}
                </MapContainer>
            </div>
            {position && (
                <p className="text-xs text-white/60">
                    {position[0].toFixed(6)}, {position[1].toFixed(6)}
                </p>
            )}
        </div>
    );
}
